/* eslint-disable react/display-name */
import type { ContractCostData } from 'gql/graphql';
import { DeviationTipEnum } from 'gql/graphql';
import type { Statistic } from 'uikit';
import { IconWarning, Text, cnMixSpace } from 'uikit';

import { useMemo } from 'react';

type DataItem = { type: string; value: number };

const ContentStyle = { display: 'flex', alignItems: 'center' };

export const useRemainderPie = (cost?: ContractCostData) => {
    const { contractPlanReminderPercent = 0, contractPlanWithTax, deviationTip } = cost || {};

    const pieData = useMemo<DataItem[]>(() => {
        const data = [];

        if ([DeviationTipEnum.None, DeviationTipEnum.LessThan30].includes(deviationTip as DeviationTipEnum)) {
            data.push({ type: 'Остаток по договору', value: Math.ceil(contractPlanReminderPercent) });
            data.push({ type: 'rest', value: 100 - Math.ceil(contractPlanReminderPercent) });
        } else {
            data.push({ type: 'rest', value: 100 });
        }

        return data;
    }, [contractPlanReminderPercent, deviationTip]);

    const pieColor = useMemo<string[]>(() => {
        const color = [];

        if (deviationTip === DeviationTipEnum.None) {
            color.push('rgba(34, 195, 142, 1)');
        } else if (deviationTip === DeviationTipEnum.LessThan30) {
            color.push('rgba(243, 139, 0, 1)');
        }

        color.push('rgba(0,66,105, .07)');

        return color;
    }, [deviationTip]);

    const pieStatistic = useMemo<Statistic<DataItem>>(
        () => ({
            title: {
                customHtml: () => (
                    <Text size="2xs" lineHeight="xs">
                        Остаток по
                        <br />
                        договору
                    </Text>
                ),
            },
            content: {
                customHtml: () => (
                    <div style={ContentStyle}>
                        {(deviationTip === DeviationTipEnum.Negative || !contractPlanWithTax) && (
                            <IconWarning
                                className={cnMixSpace({ mR: '3xs', mT: contractPlanWithTax ? undefined : '2xs' })}
                                view="alert"
                            />
                        )}
                        {contractPlanWithTax ? (
                            <Text size="2xl" view={deviationTip !== DeviationTipEnum.Negative ? 'primary' : 'alert'}>
                                {deviationTip === DeviationTipEnum.Negative
                                    ? 0
                                    : Math.ceil(contractPlanReminderPercent)}
                                %
                            </Text>
                        ) : undefined}
                    </div>
                ),
            },
        }),
        [contractPlanReminderPercent, contractPlanWithTax, deviationTip],
    );

    const pieTooltip = useMemo(() => {
        if (deviationTip === DeviationTipEnum.Negative) {
            return 'Необходимо сформировать Доп. Соглашение';
        }

        if (!contractPlanWithTax) {
            return 'Не заполнен план по договору';
        }

        return '';
    }, [contractPlanWithTax, deviationTip]);

    return useMemo(
        () => ({
            pieData,
            pieColor,
            pieStatistic,
            pieTooltip,
        }),
        [pieColor, pieData, pieStatistic, pieTooltip],
    );
};
